import { useQuery } from "@tanstack/react-query";
import { DocumentTextService } from "@/services/knowledgebase/DocumentTextService";
import type { MergePreview, MergeSourcePreview } from "@/types/knowledge/knowledge-brain";
import type { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

/**
 * Builds the proposed merged document BEFORE anything is written.
 *
 * The merge itself is a deterministic concatenation on the server: for each
 * source it emits `--- Document: <name> ---\n\n<parsed text>` and joins the
 * blocks with a blank line (see `supabase/functions/doc-upload/handlers/
 * consolidate.ts`). This preview assembles the same shape client-side from the
 * same `parsed` variant, so what the user reads is what the merge produces.
 *
 * Two honest caveats, surfaced in the UI:
 *  - each source is capped for the browser, so long documents show a head
 *    excerpt rather than their full body;
 *  - the server orders sources by its own `in(id)` fetch, so section order can
 *    differ from the order shown here.
 */

const PER_SOURCE_CHARS = 4000;

const buildMerged = (sources: MergeSourcePreview[]): string =>
  sources
    .filter((source) => (source.excerpt ?? "").length > 0)
    .map((source) => `--- Document: ${source.name} ---\n\n${source.excerpt}`)
    .join("\n\n");

export const useMergePreview = (documents: DocumentWithMetadata[], enabled: boolean) => {
  const documentIds = documents.map((doc) => doc.id);

  return useQuery<MergePreview>({
    queryKey: ["knowledge-merge-preview", documentIds],
    enabled: enabled && documentIds.length >= 2,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async () => {
      const sources: MergeSourcePreview[] = await Promise.all(
        documents.map(async (doc) => {
          try {
            const { text, characters, truncated } = await DocumentTextService.getText(
              doc,
              PER_SOURCE_CHARS,
            );
            return {
              documentId: doc.id,
              name: doc.name,
              excerpt: text,
              characters,
              truncated,
              error: text ? null : "No readable content found for this source.",
            };
          } catch {
            return {
              documentId: doc.id,
              name: doc.name,
              excerpt: "",
              characters: 0,
              truncated: false,
              error: "Could not read this source.",
            };
          }
        }),
      );

      const merged = buildMerged(sources);

      return {
        documentName: "",
        sources,
        merged,
        totalCharacters: sources.reduce((sum, source) => sum + source.characters, 0),
        truncated: sources.some((source) => source.truncated),
      };
    },
  });
};
